import React, { useEffect, useRef, useState, Fragment } from 'react'
import { Link } from 'gatsby'
import { useAuthService } from 'theme/services/auth/auth-service'
import { getEventLink } from 'theme/utils/generic-helpers'
import PropTypes from 'prop-types'
import { useFirebaseService } from 'theme/services/firebase-service'
import ApiGateway from 'theme/api/api-gateway'
import { Dialog, Transition } from '@headlessui/react'

const SessionItemRegister = ({
  sessionCapacity = 0,
  sessionId,
  conferenceId,
  workshopDetails,
  userUsedPlaces,
  userSessionLimit,
  workshopTickets,
  userTickets,
  conferenceLocation,
  userRegistration,
  recordId,
  calendarId,
  startTime,
  sessionName,
  sessionSlug,
}) => {
  const authService = useAuthService()
  const user = authService.user
  const firebase = useFirebaseService()
  const freeRequestQueue = useRef(true)
  const [registrationSession, setRegistrationSession] = useState({
    capacity: sessionCapacity,
    used: 0,
    waitingList: 0,
  })

  let [isUnregisterOpen, setIsUnregisterOpen] = useState(false)
  let [isUnregisterApprovedOpen, setIsUnregisterApprovedOpen] = useState(false)

  useEffect(() => {
    fetchRegistrationSession()
  }, [])

  const fetchRegistrationSession = async () => {
    try {
      const dbRegistrationSession = await ApiGateway.getRegistrationSession(firebase, sessionId)

      if (!dbRegistrationSession) {
        return
      }

      setRegistrationSession({
        capacity: sessionCapacity,
        used: dbRegistrationSession.used,
        waitingList: dbRegistrationSession.waitingList
          ? Object.keys(dbRegistrationSession.waitingList).length
          : 0,
      })
    } catch (e) {
      console.error(e)
    }
  }

  /// /// ACTIONS

  const registerUserWorkshopSession = async () => {
    if (allCapacityUsed() || !freeRequestQueue.current || !userHasTicketRemaining()) {
      return
    }

    freeRequestQueue.current = false

    if (isOnWaitingList()) {
      await unregisterFromWaitingList()
    }

    const transactionCompleted = await ApiGateway.registerUserWorkshopSession(
      firebase,
      conferenceId,
      sessionId,
      sessionCapacity,
      recordId,
      calendarId,
      startTime,
      sessionName,
      sessionSlug,
    )

    if (transactionCompleted) {
      const registration = {
        ...userRegistration,
        registration: {
          status: 'registered',
          timestamp: Date.now(),
        },
      }

      await ApiGateway.addUserRegistration(
        firebase,
        user.uid,
        conferenceId,
        sessionId,
        registration,
        'Workshop',
        calendarId,
        startTime,
        sessionName,
        sessionSlug,
        recordId,
      )

      // const mailOptions = {
      //   from: 'connect@uxdx.com',
      //   fromName: 'UXDX',
      //   to: user.email,
      //   toName: user.displayName,
      //   subject: "You're registered! ",
      //   text:
      //     'Hi,\n\n You have successfully registered for:\n ' +
      //     workshopDetails.name +
      //     '\n' +
      //     new Date(workshopDetails.startTime).toLocaleString('en-GB', {
      //       weekday: 'long',
      //       day: 'numeric',
      //       month: 'long',
      //       year: 'numeric',
      //       hour: '2-digit',
      //       minute: '2-digit',
      //       timeZoneName: 'short',
      //       timeZone: workshopDetails.timezone,
      //     }) +
      //     '\n' +
      //     '\n I hope you enjoy the event. \n\nThanks,\nRory',
      // }

      // await ApiGateway.sendEmail(mailOptions)
      await ApiGateway.setFavouriteSession(firebase, user.uid, sessionId, conferenceId)
    }
    await fetchRegistrationSession()

    freeRequestQueue.current = true
  }

  const unregisterFromSession = async () => {
    if (!freeRequestQueue.current) {
      return
    }

    freeRequestQueue.current = false

    const transactionCompleted = await ApiGateway.unregisterFromSession(firebase, sessionId)

    if (transactionCompleted) {
      await ApiGateway.removeFavouriteSession(firebase, user.uid, sessionId)
      await ApiGateway.removeUserRegistration(firebase, user.uid, conferenceId, sessionId)
      await fetchRegistrationSession()
    }

    freeRequestQueue.current = true
  }

  const unregisterFromApprovedList = async () => {
    if (!freeRequestQueue.current) {
      return
    }

    freeRequestQueue.current = false

    const transactionCompleted = await ApiGateway.unregisterFromApprovedList(firebase, sessionId, user.uid)

    if (transactionCompleted) {
      await ApiGateway.removeFavouriteSession(firebase, user.uid, sessionId)
      await ApiGateway.removeUserRegistration(firebase, user.uid, conferenceId, sessionId)
    }

    freeRequestQueue.current = true
  }

  const registerToWaitingList = async () => {
    if (!freeRequestQueue.current) {
      return
    }
    freeRequestQueue.current = false

    await ApiGateway.registerToWaitingList(
      firebase,
      user.uid,
      conferenceId,
      sessionId,
      sessionCapacity,
      'Workshop',
      recordId,
      calendarId,
      startTime,
      sessionName,
      sessionSlug,
    )
    await ApiGateway.setFavouriteSession(firebase, user.uid, sessionId, conferenceId)

    const registrationItem = {
      ...userRegistration,
      registration: {
        status: 'waiting',
        timestamp: Date.now(),
      },
    }

    await ApiGateway.addUserRegistration(
      firebase,
      user.uid,
      conferenceId,
      sessionId,
      registrationItem,
      'Workshop',
      calendarId,
      startTime,
      sessionName,
      sessionSlug,
      recordId
    )
    await fetchRegistrationSession()

    freeRequestQueue.current = true
  }

  const unregisterFromWaitingList = async () => {
    if (!freeRequestQueue.current) {
      return
    }

    freeRequestQueue.current = false

    await ApiGateway.unregisterFromWaitingList(firebase, sessionId, user.uid)
    await ApiGateway.removeFavouriteSession(firebase, user.uid, sessionId)
    await ApiGateway.removeUserRegistration(firebase, user.uid, conferenceId, sessionId)

    await fetchRegistrationSession()
    freeRequestQueue.current = true
  }

  /// /// VARIABLES
  const userHasTicketRemaining = () => {
    const used = userUsedPlaces ?? 0
    return used < userSessionLimit
  }
  const allCapacityUsed = () => {
    return registrationSession.used >= registrationSession.capacity
  }

  const isOnWaitingList = () => {
    return userRegistration && userRegistration.registration?.status === 'waiting'
  }

  const isOnApprovedList = () => {
    return userRegistration && userRegistration.registration?.status === 'approved'
  }

  const isRegistered = () => {
    return userRegistration && userRegistration.registration?.status === 'registered'
  }

  // const showRegisterButton = () => {
  //   return !allCapacityUsed() || isRegistered()
  // }

  // const showWaitingListPart = () => {
  //   return (
  //     (((!allCapacityUsed() && !userHasTicketRemaining()) || allCapacityUsed()) &&
  //     !isRegistered()) ||
  //     isOnWaitingList()
  //   )
  // }

  // const renderButtonText = () => {
  //   if (!userRegistration) {
  //     return <>Register Now <span className='text-sm'><br />({registrationSession.capacity - registrationSession.used} spaces)</span></>
  //   }

  //   if (isOnWaitingList()) {
  //     return (
  //       <div className='flex'>
  //         On Waiting List
  //         <svg className='w-5 h-5 ml-2' fill='none' stroke='currentColor' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' /></svg>
  //       </div>
  //     )
  //   }

  //   return <>Registered </>
  // }
  const spaces = registrationSession.capacity - registrationSession.used
  // console.log('spaces', spaces, registrationSession)
  return (
    <div>
      {workshopTickets.length === 0 && conferenceId && conferenceLocation && userTickets.length > 0 && (
        // if has no workshop ticket
        <Link
          to={getEventLink(conferenceId, conferenceLocation, 'tickets')}
          className="text-sm ml-4 py-1 px-2 inline-block text-center rounded border-2 border-orange-400 text-orange-600 hover:bg-orange-200 hover:text-orange-800 focus:ring-2 focus:ring-orange-200 focus:bg-orange-200 focus:text-orange-500 active:bg-orange-200 active:text-orange-500 transition duration-150 ease-in-out"
          data-cy="session-buy-ticket-button"
        >
          Upgrade for Access
          {spaces < 6 && (
            <span className="text-sm">
              <br />({spaces} space<span className={`${spaces === 1 ? 'hidden' : ''}`}>s</span> remaining)
            </span>
          )}
        </Link>
      )}
      {/* {userTickets.length == 0 && (
        // if has no workshop ticket
        <div className="text-sm ml-4 py-1 px-2 text-orange-500 font-bold border-2 border-orange-500 rounded">
          Workshop Ticket Only
        </div>
      )} */}
      {user && isRegistered() && (
        <div className="flex items-center">
          <button
            onClick={() => {
              if (allCapacityUsed() && registrationSession.waitingList > 0) {
                setIsUnregisterOpen(true)
              } else {
                unregisterFromSession()
              }
            }}
            className="text-sm ml-4 py-1 px-2 rounded border-2 border-green-700 bg-green-700 hover:bg-red-800 text-white hover:border-red-800 focus:ring-2 focus:ring-green-200 focus:bg-green-200 focus:text-green-500 active:bg-green-200 active:text-green-500 transition duration-150 ease-in-out"
            data-cy="session-register-button"
          >
            <div className="flex">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              You&apos;re Registered
            </div>
            <span className="text-xs text-gray-50">Click to Unregister</span>
          </button>
          <Transition appear show={isUnregisterOpen} as={Fragment}>
            <Dialog
              as="div"
              className="fixed inset-0 z-10 overflow-y-auto"
              onClose={() => setIsUnregisterOpen(false)}
            >
              <div className="min-h-screen px-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Dialog.Overlay className="fixed inset-0" />
                </Transition.Child>

                {/* This element is to trick the browser into centering the modal contents. */}
                <span className="inline-block h-screen align-middle" aria-hidden="true">
                  &#8203;
                </span>
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Are you sure you want to unregister?
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        You will not be able to re-register because the workshop is full and your space will
                        be offered out.
                      </p>
                    </div>

                    <div className="mt-4">
                      <button
                        type="button"
                        data-cy="popup-unregister-button"
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-red-900 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500"
                        onClick={unregisterFromSession}
                      >
                        Unregister
                      </button>
                      <button
                        type="button"
                        className="inline-flex justify-center px-4 py-2 ml-2 text-sm font-medium text-blue-900 bg-blue-100 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                        onClick={() => setIsUnregisterOpen(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition>
        </div>
      )}
      {user && isOnApprovedList() && (
        <div className="flex items-center">
          <p className="text-sm text-gray-400 flex pb-1 pl-2">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            You&apos;re registered
          </p>
          <button
            onClick={() => {
              if (!userHasTicketRemaining() || allCapacityUsed()) {
                setIsUnregisterApprovedOpen(true)
              } else {
                unregisterFromApprovedList()
              }
            }}
            className="text-sm ml-4 py-1 px-2 rounded border-2 border-gray-400 text-gray-600 hover:bg-gray-200 hover:text-gray-800 focus:ring-2 focus:ring-gray-200 focus:bg-gray-200 focus:text-gray-500 active:bg-gray-200 active:text-green-500 transition duration-150 ease-in-out"
            data-cy="session-register-button"
          >
            Unregister
          </button>
          <Transition appear show={isUnregisterApprovedOpen} as={Fragment}>
            <Dialog
              as="div"
              className="fixed inset-0 z-10 overflow-y-auto"
              onClose={() => setIsUnregisterApprovedOpen(false)}
            >
              <div className="min-h-screen px-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Dialog.Overlay className="fixed inset-0" />
                </Transition.Child>

                {/* This element is to trick the browser into centering the modal contents. */}
                <span className="inline-block h-screen align-middle" aria-hidden="true">
                  &#8203;
                </span>
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Are you sure you want to unregister?
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        You will not be able to re-register because
                        {!userHasTicketRemaining()
                          ? 'you are over your limit of workshops.'
                          : 'the workshop is full'}
                        .
                      </p>
                    </div>

                    <div className="mt-4">
                      <button
                        type="button"
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-red-900 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500"
                        onClick={unregisterFromApprovedList}
                      >
                        Unregister
                      </button>
                      <button
                        type="button"
                        className="inline-flex justify-center px-4 py-2 ml-2 text-sm font-medium text-blue-900 bg-blue-100 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                        onClick={() => setIsUnregisterApprovedOpen(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition>
        </div>
      )}
      {user && isOnWaitingList() && (
        // cannot upgrade
        <div className="flex items-center">
          <p className="text-sm text-gray-400 flex pb-1 pl-2">
            <svg
              className="w-5 h-5 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
              />
            </svg>
            You&apos;re on the waiting list
          </p>
          <button
            onClick={unregisterFromWaitingList}
            className="text-sm ml-4 py-1 px-2 rounded border-2 border-gray-400 text-gray-600 hover:bg-gray-200 hover:text-gray-800 focus:ring-2 focus:ring-gray-200 focus:bg-gray-200 focus:text-gray-500 active:bg-gray-200 active:text-gray-500 transition duration-150 ease-in-out"
            data-cy="session-register-button"
          >
            <span className="flex">Leave Waiting List</span>
          </button>
        </div>

        // can upgrade?
      )}
      {user &&
        !isOnWaitingList() &&
        !isRegistered() &&
        !isOnApprovedList() &&
        workshopTickets.length > 0 &&
        userHasTicketRemaining() &&
        !allCapacityUsed() && (
          // register
          <button
            onClick={registerUserWorkshopSession}
            className="text-sm ml-4 py-1 px-2 rounded border-2 border-orange-400 text-orange-600 hover:bg-orange-200 hover:text-orange-800 focus:ring-2 focus:ring-orange-200 focus:bg-orange-200 focus:text-orange-500 active:bg-orange-200 active:text-orange-500 transition duration-150 ease-in-out"
            data-cy="session-register-button"
          >
            Register Now {/* {spaces < 6 && ( */}
            {spaces < 6 && (
              <span className="text-sm">
                <br />({spaces} space<span className={`${spaces === 1 ? 'hidden' : ''}`}>s</span> remaining)
              </span>
            )}
            {/* )} */}
          </button>
        )}
      {user &&
        !isOnWaitingList() &&
        !isRegistered() &&
        !isOnApprovedList() &&
        workshopTickets.length > 0 &&
        (!userHasTicketRemaining() || allCapacityUsed()) && (
          // join waiting list
          <div className="flex items-center">
            {/* <p className="text-sm text-gray-400 flex pb-1 pl-2">
              {allCapacityUsed() && <>Sorry, this workshop is full!</>}
              {!userHasTicketRemaining() && !allCapacityUsed() && (
                <>You have used your {userSessionLimit} workshops</>
              )}
            </p> */}
            {allCapacityUsed() && userHasTicketRemaining() && (
              <button
                onClick={registerToWaitingList}
                className="text-sm ml-4 py-1 px-2 rounded border-2 border-red-400 text-red-600 hover:bg-red-200 hover:text-red-800 focus:ring-2 focus:ring-red-200 focus:bg-red-200 focus:text-red-500 active:bg-red-200 active:text-red-500 transition duration-150 ease-in-out"
                data-cy="session-register-button"
              >
                Registrations Full{' '}
                {registrationSession.waitingList > 0 && (
                  <span className="text-sm">
                    <br />
                    (Join {registrationSession.waitingList} on waitlist)
                  </span>
                )}
              </button>
            )}
            {!allCapacityUsed() && !userHasTicketRemaining() && (
              <button
                disabled={true}
                className="text-sm ml-4 py-1 px-2 text-gray-600 "
                data-cy="session-register-button"
              >
                Registration disabled
                <span className="text-xs">
                  <br />
                  You&apos;ve used your {userSessionLimit} workshops
                </span>
              </button>
            )}
            {!userHasTicketRemaining() && allCapacityUsed() && (
              <button
                disabled={true}
                className="text-sm ml-4 py-1 px-2 text-gray-600 "
                data-cy="session-register-button"
              >
                Registrations Full
                <span className="text-xs">
                  <br />
                  You&apos;ve used your {userSessionLimit} workshops
                </span>
              </button>
            )}
          </div>
        )}
    </div>
  )
}

SessionItemRegister.propTypes = {
  sessionCapacity: PropTypes.number.isRequired,
  sessionId: PropTypes.string.isRequired,
  conferenceId: PropTypes.string.isRequired,
  workshopDetails: PropTypes.object.isRequired,
  userUsedPlaces: PropTypes.number,
  userSessionLimit: PropTypes.number,
  workshopTickets: PropTypes.array.isRequired,
  userTickets: PropTypes.array.isRequired,
  userRegistration: PropTypes.object,
  calendarId: PropTypes.string,
  startTime: PropTypes.string,
  sessionName: PropTypes.string,
}

export default SessionItemRegister
